// FLAG:useDocuAi — whether the invoice-review UI should explain a gross-to-net calculation.
export interface GrossTaxCalculationFact {
  method?: string | null
  roundingWarning?: boolean | null
  lineTotalsAreGross?: boolean | null
  filledMissingPriceNet?: boolean | null
  filledMissingVatAmount?: boolean | null
  matchesExtractedTotals?: {
    net?: boolean | null
    vat?: boolean | null
    gross?: boolean | null
  } | null
}

const isGrossPositionDerivation = (
  fact?: GrossTaxCalculationFact | null,
): boolean => fact?.method === 'gross_to_net_from_gross_positions'

// The headline claims that net and VAT were calculated from gross amounts.
// Only true when the back-calculation actually SUPPLIED a headline amount the document
// does not state — the fill markers. An invoice that prints its own net/VAT ran the
// calculation as a consistency check only, and saying otherwise is simply wrong
// (Strato.pdf, issue 94222e33).
export const shouldShowGrossTaxCalculationHint = (
  fact?: GrossTaxCalculationFact | null,
): boolean => isGrossPositionDerivation(fact)
  && (
    fact!.filledMissingPriceNet === true
    || fact!.filledMissingVatAmount === true
  )

// The POSITION claim, a second and independent reason: the invoice PRINTS gross line
// amounts, so the net/VAT shown per position were computed here — calculatePosition
// divides each line by its own rate and rounds it on its own. With two or more such
// lines Σ round(…) can differ by a cent from round(Σ …), and the reviewer books those
// per-line figures. That risk exists whether or not the headline totals are printed,
// which is why it is NOT expressed by loosening the headline predicate above.
//
// A single gross line cannot drift (net = round(gross/rate), tax = gross − net
// reproduces the headline exactly), so the one-position case stays silent — that is
// Strato, and keeping it silent is what makes this additive rather than a revert.
export const shouldShowGrossPositionsRoundingHint = (
  fact?: GrossTaxCalculationFact | null,
  grossPositionCount = 0,
): boolean => isGrossPositionDerivation(fact)
  && fact!.lineTotalsAreGross === true
  && grossPositionCount >= 2
